import { formatDate } from '@pb/utils'

import {
  NormalizedOnlineOrder,
  NormalizedOnlineOrderDetails,
  OnlineOrders,
  StoreOrders,
} from './types'

export const normalizeOnlineOrders = ({ orders }: OnlineOrders): NormalizedOnlineOrder[] =>
  orders.map(({ code: orderId, statusCode, timestamp, totalAmount, statusDisplay, ...rest }) => {
    const isArchivedOrder = !statusDisplay && !totalAmount && !statusCode
    return {
      statusCode,
      statusDisplay,
      orderId,
      orderType: 'online',
      isLoading: false,
      failedToLoad: false,
      totalAmount,
      isArchivedOrder,
      isCancelled: statusCode === 'CANCELLED',
      date: timestamp && formatDate(timestamp),
      timestamp,
      ...rest,
    }
  })

// Renames some keys and adds orderType and isLoading
export const normalizeStoreOrders = ({ receipts = [] }: StoreOrders): NormalizedOnlineOrder[] =>
  receipts.map(({ itemId: orderId, status: statusDisplay, timestamp, ...rest }) => ({
    orderId,
    orderType: 'store',
    isLoading: false,
    failedToLoad: false,
    statusDisplay,
    date: timestamp && formatDate(timestamp),
    ...rest,
  }))

// Group packages together, renames the key 'code' to 'orderId', and add orderType and prepend base url to image path (empty if we're on isDemo env)
export const normalizeOnlineOrder = ({
  code: orderId,
  splitOrderInfoMap,
  splitOrderDeliveryInfoMap,
  consignments = [],
  deliveryMode,
  deliveryAddress,
  statusDisplay,
  statusCode,
  orderStatusInfo,
  timestamp,
  ...rest
}: any): NormalizedOnlineOrderDetails => {
  let cancelledEntries = []
  let entries = []

  return {
    orderId,
    orderType: 'online',
    orderDate: timestamp && formatDate(timestamp),
    packages: Object.entries(consignments).map((index, i) => {
      cancelledEntries = consignments[i].cancelledEntries?.map((item) => {
        item.orderEntry.isItemNotDeliverable = true
        return item.orderEntry
      })

      entries = consignments[i].entries?.map((item) => {
        return item.orderEntry
      })

      const packageInfo =
        {
          deliveryMode: consignments[i].deliveryMode,
          deliveryAddress: consignments[i].deliveryAddress,
        } || {}

      return {
        packageNumber: i + 1,
        deliveryMethod: packageInfo.deliveryMode?.name,
        estimatedDeliveryTime: packageInfo.deliveryMode?.deliveryPromiseTime,
        deliveryAddress: packageInfo.deliveryAddress || {},
        entries,
        cancelledEntries,
        status: consignments[i].statusDisplay,
        statusBar: consignments[i].statusBar,
        trackingId: consignments[i].entries?.[0]?.trackingId,
        trackingUrl: consignments[i].entries?.[0]?.trackingUrl,
        resellProduct: consignments[i].entries?.[0]?.orderEntry?.resellProduct,
        returnUrl: consignments[i].returnUrl,
        carrierName: consignments[i].carrierName,
        brand: consignments[i].entries?.[0]?.orderEntry?.brand,
      }
    }),
    isCancelled: statusCode === 'CANCELLED' || orderStatusInfo === 'Cancelled',
    // orderStatusInfo reflects immediately but is only present in GET/orders/:orderId
    // statusCode can be delayed upon cancellation, but is present in both GET/orders/:orderId and GET/orders
    ...rest,
    codeImg: rest.codeImg ? __BASE_HOST_URL__ + rest.codeImg : '',
  }
}

// Convert the addresses array into an object with the store codes as keys
export const normalizeStoreAddresses = (addresses): any =>
  addresses.reduce((storeCodeAddressMap, address) => {
    return {
      ...storeCodeAddressMap,
      [address.storeCode]: address,
    }
  }, {})

// Renames the key 'itemId' to 'orderId'
export const normalizeStoreOrder = ({ itemId: orderId, timestamp, ...rest }): any => ({
  orderId,
  orderType: 'store',
  ...rest,
  codeImg: rest.codeImg ? __BASE_HOST_URL__ + rest.codeImg : '',
  orderDate: timestamp && formatDate(timestamp),
})
